import React from "react";

interface PageNumberRendererProps {
  children: React.ReactNode;
  handleLinkClick?: (
    href: string,
    pageNum: number,
    event: React.MouseEvent<HTMLAnchorElement>,
    paragraph: string
  ) => void;
}

const PageNumberRenderer: React.FC<PageNumberRendererProps> = ({
  children,
  handleLinkClick,
}) => {
  const childArray = React.Children.toArray(children);

  // Assume the document link is the first hyperlink that appears
  let linkUrl: string | undefined = undefined;

  // Iterate over children to format text while preserving elements like links
  const formattedContent = childArray.flatMap((child) => {
    if (React.isValidElement<{ href?: string }>(child) && child.props?.href) {
      // Capture the href link of the document link
      linkUrl = child.props.href;
      return child; // Return the hyperlink element without changes
    }

    if (typeof child === "string") {
      // Regular expression to capture page and paragraph numbers in the pattern (p. [13,25-26], [17,25])
      const pattern: RegExp =
        /\(p\. ((\[\s*(\d+),\s*([^\]]+)\s*\](,\s*)?)+)\)/g;

      // Replace matched numbers with formatted content using captured link URL
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;

      child.replace(pattern, (match, fullMatch) => {
        // Push text before the match
        const offset = child.indexOf(match, lastIndex);
        if (offset > lastIndex) {
          parts.push(child.substring(lastIndex, offset));
        }

        if (linkUrl) {
          // Capture all [page, paragraph] pairs
          const pageParagraphPairs = fullMatch.match(
            /\[\s*(\d+),\s*([^\]]+)\s*\]/g
          );

          const formattedLinks = pageParagraphPairs?.map(
            (pair: string, idx: number) => {
              const match = pair.match(/\[\s*(\d+),\s*([^\]]+)\s*\]/);
              if (match) {
                const page = match[1]; // First part as page number
                const paragraph = match[2]; // Rest as a single string for paragraphs
                
                return (
                  <a
                    key={`link-${linkUrl}-${idx}`}
                    href={linkUrl}
                    className="page-link"
                    data-page={page.trim()}
                    data-paragraph={paragraph.trim()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();

                      const hrefValue = e.currentTarget.href;

                      if (handleLinkClick) {
                        handleLinkClick(
                          hrefValue,
                          Number(page.trim()),
                          e,
                          paragraph.trim()
                        );
                      }
                    }}
                  >
                    {`${page.trim()}`}
                  </a>
                );
              }
              return null;
            }
          );

          // Directly push the links without the (p. ... ) part
          formattedLinks?.forEach((element: React.ReactNode[], idx: number) => {
            if (idx > 0) {
              parts.push(" ");
            }
            parts.push(element);
          });
        } else {
          // If no linkUrl is found, return as normal text
          parts.push(match);
        }

        lastIndex = offset + match.length;
        return match;
      });

      // Push remaining text after the last match
      if (lastIndex < child.length) {
        parts.push(child.substring(lastIndex));
      }

      return parts;
    }

    // Return other elements without modification
    return child;
  });

  return <>{formattedContent}</>;
};

export default PageNumberRenderer;

